import type { SchaefflerFormDTO } from "~/features/cms/types/dto";
import { renderCMSComponent } from "~/features/cms/componentFactory";
import { Button, Stack } from "~/common/mui";
import { FormProvider, useForm } from "react-hook-form";
import { resolveApiUrl } from "~/utils/url/urlResolver";
import { useState } from "react";
import { getCsrfValue } from "~/common/utils/ApiHelper";
import { trackSubmit } from "~/features/cms/schaefflerForm/analytics";
import { Snackbar } from "~/common/mui/Snackbar/Snackbar";
import { useTranslation } from "react-i18next";
import { Alert } from "~/common/mui/Alert/Alert";
import { makeExternallyStyledBaseElement } from "~/features/cms/utils/styles";

export interface SchaefflerFormProps extends Omit<SchaefflerFormDTO, "type"> {
  className?: string;
  onSuccess?: () => void;
}

export const SchaefflerForm = (props: SchaefflerFormProps) => {
  const { t } = useTranslation();
  const children = [...props.hiddenFields, ...props.rows];

  const formProps = useForm();
  const [loading, setLoading] = useState(false);
  const [showSnackbar, setShowSnackbar] = useState(false);

  return (
    <FormProvider {...formProps}>
      <Form
        rootStyles={props.rootStyles}
        noValidate
        onSubmit={formProps.handleSubmit(
          async (values) => {
            setLoading(true);
            try {
              await apiSubmitForm(values);
              trackSubmit(true);
              setShowSnackbar(true);
              props.onSuccess?.();
              formProps.reset();
            } catch {
              trackSubmit(false);
            } finally {
              setLoading(false);
            }
          },
          () => trackSubmit(false),
        )}
      >
        <Stack gap={4}>
          {children.map((item, index) => renderCMSComponent(item, undefined, index))}
          <Button type="submit" loading={loading}>
            {t("contactUsPage.form.ok")}
          </Button>
        </Stack>
      </Form>
      <Snackbar open={showSnackbar} onClose={() => setShowSnackbar(false)}>
        <Alert severity="success" onClose={() => setShowSnackbar(false)} sx={{ boxShadow: 2 }}>
          {t("feature.cmsContactForm.success")}
        </Alert>
      </Snackbar>
    </FormProvider>
  );
};

const Form = makeExternallyStyledBaseElement("form");

const apiSubmitForm = async (data: Record<string, string>): Promise<void> => {
  const url = resolveApiUrl(`/api/contact-form/submit`);
  const formData = new FormData();
  Object.entries(data).forEach(([key, value]) => formData.append(key, value));
  const response = await fetch(url, {
    method: "POST",
    body: formData,
    credentials: "same-origin", // include, omit
    mode: "cors", // no-cors, same-origin
    referrer: "no-referrer", // client
    headers: {
      CSRFToken: getCsrfValue(),
    },
  });
  if (!response.ok) {
    throw new Error("Error submitting form");
  }
};
